import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  History, Search, Filter, FileText, Calendar, User, 
  ChevronDown, ChevronRight, Eye, Package, Truck, 
  Building2, DollarSign, CheckCircle, AlertTriangle,
  CloudUpload, Share2, ClipboardCheck, Upload, RefreshCw,
  Webhook, Terminal, Zap, ShoppingBag, CreditCard, Activity,
  MessageSquare, Phone, Send, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTION_ICONS = {
  created: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  updated: { icon: RefreshCw, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  status_changed: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10' },
  cancelled: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
  invoice_uploaded: { icon: Upload, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  share_link_created: { icon: Share2, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  pickup_assigned: { icon: Truck, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  pickup_status_updated: { icon: Truck, color: 'text-teal-400', bg: 'bg-teal-500/10' },
  pod_uploaded: { icon: Upload, color: 'text-green-400', bg: 'bg-green-500/10' },
  grn_submitted: { icon: ClipboardCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  zoho_synced: { icon: CloudUpload, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  zoho_sync_failed: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
  payment_recorded: { icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10' },
  default: { icon: History, color: 'text-gray-400', bg: 'bg-gray-500/10' }
};

const ACTION_LABELS = {
  created: 'Created',
  updated: 'Updated',
  status_changed: 'Status Changed',
  cancelled: 'Cancelled',
  invoice_uploaded: 'Invoice Uploaded',
  share_link_created: 'Share Link Created',
  pickup_assigned: 'Pickup Assigned',
  pickup_status_updated: 'Pickup Status Updated',
  pod_uploaded: 'POD Uploaded',
  grn_submitted: 'GRN Submitted',
  zoho_synced: 'Zoho Synced',
  zoho_sync_failed: 'Zoho Sync Failed',
  payment_recorded: 'Payment Recorded'
};

const SOURCE_ICONS = {
  mamopay: { icon: CreditCard, color: 'text-green-400', bg: 'bg-green-500/10', label: 'MamoPay' },
  shopify: { icon: ShoppingBag, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Shopify' },
  zoho: { icon: CloudUpload, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Zoho' },
  default: { icon: Webhook, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Unknown' }
};

const LEVEL_COLORS = {
  ERROR: 'text-red-400 bg-red-500/10',
  WARNING: 'text-yellow-400 bg-yellow-500/10',
  INFO: 'text-blue-400 bg-blue-500/10',
  DEBUG: 'text-gray-400 bg-gray-500/10'
};

const AuditLogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('audit');
  
  // Audit logs state
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('7'); // days
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  
  // Webhook logs state
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookSourceFilter, setWebhookSourceFilter] = useState('all');
  const [webhookDaysFilter, setWebhookDaysFilter] = useState('7');
  const [selectedWebhookLog, setSelectedWebhookLog] = useState(null);
  const [webhookDetailOpen, setWebhookDetailOpen] = useState(false);
  const [webhookStats, setWebhookStats] = useState({});
  
  // System logs state
  const [systemLogs, setSystemLogs] = useState([]);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemCategoryFilter, setSystemCategoryFilter] = useState('all');
  const [systemLevelFilter, setSystemLevelFilter] = useState('all');
  const [systemSearchTerm, setSystemSearchTerm] = useState('');
  
  // Integration status
  const [integrationStatus, setIntegrationStatus] = useState(null);
  
  // Journey logs state
  const [journeyLogs, setJourneyLogs] = useState({ executions: [], messages: [], errors: [] });
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyPhoneSearch, setJourneyPhoneSearch] = useState('');
  const [journeyDaysFilter, setJourneyDaysFilter] = useState('7');
  const [journeyStatusFilter, setJourneyStatusFilter] = useState('all');
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetailOpen, setExecutionDetailOpen] = useState(false);
  // "Why didn't this send?" diagnostics — auto-loaded when the
  // execution detail popup opens. Surfaces template approval status,
  // 24-h window, cooldown blocks, BSP/Meta API errors etc. so the
  // operator doesn't have to cross-reference 4 different pages.
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  // Shopify sync logs state (outbound Shopify fulfillment attempts)
  const [shopifyLogs, setShopifyLogs] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyStatusFilter, setShopifyStatusFilter] = useState('all');
  const [shopifyDaysFilter, setShopifyDaysFilter] = useState('7');
  const [shopifySearch, setShopifySearch] = useState('');
  const [shopifyStatusCounts, setShopifyStatusCounts] = useState({ success: 0, failed: 0 });
  const [selectedShopifyLog, setSelectedShopifyLog] = useState(null);
  const [shopifyDetailOpen, setShopifyDetailOpen] = useState(false);

  // Channel sync logs (Danube / Noon stock + order pushes)
  const [channelLogs, setChannelLogs] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');
  const [channelStatusFilter, setChannelStatusFilter] = useState('all');
  const [channelDaysFilter, setChannelDaysFilter] = useState('7');
  const [channelStatusCounts, setChannelStatusCounts] = useState({ success: 0, failed: 0 });

  useEffect(() => {
    loadAuditLogs();
  }, [actionFilter, dateFilter, pagination.page]);
  
  useEffect(() => {
    if (activeTab === 'webhooks') {
      loadWebhookLogs();
    } else if (activeTab === 'system') {
      loadSystemLogs();
    } else if (activeTab === 'status') {
      loadIntegrationStatus();
    } else if (activeTab === 'journeys') {
      loadJourneyLogs();
    } else if (activeTab === 'shopify_sync') {
      loadShopifyLogs();
    } else if (activeTab === 'channel_sync') {
      loadChannelLogs();
    }
  }, [activeTab, webhookSourceFilter, webhookDaysFilter, systemCategoryFilter, systemLevelFilter, journeyDaysFilter, journeyStatusFilter, shopifyStatusFilter, shopifyDaysFilter, channelFilter, channelStatusFilter, channelDaysFilter]);

  // Auto-load the "Why didn't this send?" diagnostics whenever the
  // execution detail popup opens for a new execution. Cleared when
  // the popup closes so each open re-fetches fresh data.
  useEffect(() => {
    if (!executionDetailOpen || !selectedExecution?.id) {
      setDiagnostics(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDiagnosticsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${API_BASE_URL}/v1/journeys/executions/${selectedExecution.id}/diagnostics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setDiagnostics(r.data);
      } catch (err) {
        if (!cancelled) setDiagnostics({ reasons: [{ severity: 'error', label: 'Could not load diagnostics', hint: err.response?.data?.detail || err.message }] });
      } finally {
        if (!cancelled) setDiagnosticsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [executionDetailOpen, selectedExecution?.id]);

  const loadAuditLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', pagination.limit);
      params.append('skip', (pagination.page - 1) * pagination.limit);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (dateFilter !== 'all') params.append('days', dateFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };
  
  const loadWebhookLogs = useCallback(async () => {
    setWebhookLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (webhookSourceFilter !== 'all') params.append('source', webhookSourceFilter);
      if (webhookDaysFilter !== 'all') params.append('days', webhookDaysFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API_BASE_URL}/admin/webhook-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWebhookLogs(response.data.logs || []);
      setWebhookStats(response.data.sources || {});
    } catch (error) {
      console.error('Failed to load webhook logs:', error);
      toast.error('Failed to load webhook logs');
    } finally {
      setWebhookLoading(false);
    }
  }, [webhookSourceFilter, webhookDaysFilter]);

  // Outbound Shopify sync (fulfillment create / skip / error) —
  // answers the "why didn't this order fulfill on Shopify?" question
  // from the shared audit-log collection. Debounced indirectly via
  // the `activeTab` effect hook above.
  const loadShopifyLogs = useCallback(async () => {
    setShopifyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (shopifyStatusFilter !== 'all') params.append('status', shopifyStatusFilter);
      if (shopifyDaysFilter !== 'all') params.append('days', shopifyDaysFilter);
      if (shopifySearch) params.append('search', shopifySearch);
      params.append('limit', '300');

      const response = await axios.get(`${API_BASE_URL}/admin/shopify-sync-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShopifyLogs(response.data.logs || []);
      setShopifyStatusCounts(response.data.status_counts || { success: 0, failed: 0 });
    } catch (error) {
      console.error('Failed to load Shopify sync logs:', error);
      toast.error('Failed to load Shopify sync logs');
    } finally {
      setShopifyLoading(false);
    }
  }, [shopifyStatusFilter, shopifyDaysFilter, shopifySearch]);

  // Marketplace channel (Danube / Noon) stock + order push results.
  const loadChannelLogs = useCallback(async () => {
    setChannelLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (channelStatusFilter !== 'all') params.append('status', channelStatusFilter);
      if (channelDaysFilter !== 'all') params.append('days', channelDaysFilter);
      params.append('limit', '200');

      const response = await axios.get(`${API_BASE_URL}/admin/channel-sync-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannelLogs(response.data.logs || []);
      setChannelStatusCounts(response.data.status_counts || { success: 0, failed: 0 });
    } catch (error) {
      console.error('Failed to load channel sync logs:', error);
      toast.error('Failed to load channel sync logs');
    } finally {
      setChannelLoading(false);
    }
  }, [channelFilter, channelStatusFilter, channelDaysFilter]);
  
  const loadSystemLogs = useCallback(async () => {
    setSystemLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (systemCategoryFilter !== 'all') params.append('category', systemCategoryFilter);
      if (systemLevelFilter !== 'all') params.append('level', systemLevelFilter);
      if (systemSearchTerm) params.append('search', systemSearchTerm);
      params.append('lines', '200');
      
      const response = await axios.get(`${API_BASE_URL}/admin/system-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSystemLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to load system logs:', error);
      toast.error('Failed to load system logs');
    } finally {
      setSystemLoading(false);
    }
  }, [systemCategoryFilter, systemLevelFilter, systemSearchTerm]);
  
  const loadIntegrationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/integration-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    }
  };

  const loadJourneyLogs = useCallback(async () => {
    setJourneyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (journeyDaysFilter !== 'all') params.append('days', journeyDaysFilter);
      if (journeyStatusFilter !== 'all') params.append('status', journeyStatusFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API_BASE_URL}/v1/journeys/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJourneyLogs({
        executions: response.data.executions || [],
        messages: response.data.messages || [],
        errors: response.data.errors || [],
        stats: response.data.stats || {}
      });
    } catch (error) {
      console.error('Failed to load journey logs:', error);
      toast.error('Failed to load journey logs');
    } finally {
      setJourneyLoading(false);
    }
  }, [journeyDaysFilter, journeyStatusFilter]);

  const searchJourneyByPhone = async () => {
    if (!journeyPhoneSearch.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    setJourneyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/v1/journeys/debug/messages-by-phone/${encodeURIComponent(journeyPhoneSearch)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setJourneyLogs({
        executions: response.data.journey_executions || [],
        messages: response.data.whatsapp_messages || [],
        errors: response.data.execution_errors || [],
        stats: response.data.summary || {}
      });
      
      if (response.data.summary?.total_executions === 0) {
        toast.info('No journey data found for this phone number');
      }
    } catch (error) {
      console.error('Failed to search journey logs:', error);
      toast.error('Failed to search journey logs');
    } finally {
      setJourneyLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadAuditLogs();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionConfig = (action) => {
    return ACTION_ICONS[action] || ACTION_ICONS.default;
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  if (loading && activeTab === 'audit') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }
  
  const getSourceConfig = (source) => {
    return SOURCE_ICONS[source] || SOURCE_ICONS.default;
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary flex items-center gap-3">
            <History className="h-8 w-8 text-wt-gold" />
            Logs & Debugging
          </h1>
          <p className="text-wt-text-muted mt-1">Track system activities, webhooks, and debug integrations</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-wt-subtle border border-wt-border mb-4">
          <TabsTrigger value="audit" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <History className="h-4 w-4 mr-2" />
            Audit Logs
          </TabsTrigger>
          <TabsTrigger value="journeys" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <MessageSquare className="h-4 w-4 mr-2" />
            Journey Logs
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Webhook className="h-4 w-4 mr-2" />
            Webhook Logs
          </TabsTrigger>
          <TabsTrigger value="shopify_sync" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black" data-testid="audit-tab-shopify-sync">
            <ShoppingBag className="h-4 w-4 mr-2" />
            Shopify Sync
          </TabsTrigger>
          <TabsTrigger value="channel_sync" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black" data-testid="audit-tab-channel-sync">
            <Zap className="h-4 w-4 mr-2" />
            Channel Sync
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Terminal className="h-4 w-4 mr-2" />
            System Logs
          </TabsTrigger>
          <TabsTrigger value="status" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Activity className="h-4 w-4 mr-2" />
            Integration Status
          </TabsTrigger>
        </TabsList>
        
        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={loadAuditLogs} 
              variant="outline" 
              className="border-wt-border text-wt-text-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

      {/* Filters */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
              <Input
                placeholder="Search by PO number, details..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="audit-search-input"
              />
            </div>
          </div>
          
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border">
              <SelectItem value="all">All Actions</SelectItem>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border">
              <SelectItem value="1">Last 24 Hours</SelectItem>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <History className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Events</p>
              <p className="text-2xl font-bold text-wt-text-primary">{pagination.total}</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Status Changes</p>
              <p className="text-2xl font-bold text-wt-text-primary">
                {logs.filter(l => l.action === 'status_changed').length}
              </p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <CloudUpload className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Zoho Syncs</p>
              <p className="text-2xl font-bold text-wt-text-primary">
                {logs.filter(l => l.action === 'zoho_synced').length}
              </p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <Truck className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Pickup Updates</p>
              <p className="text-2xl font-bold text-wt-text-primary">
                {logs.filter(l => l.action.includes('pickup')).length}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle">
              <tr>
                <th className="text-left p-4 text-wt-text-muted font-medium">Timestamp</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Action</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">PO Number</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Details</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">User</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => {
                const config = getActionConfig(log.action);
                const Icon = config.icon;
                
                return (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-t border-wt-border/50 hover:bg-wt-subtle/50 cursor-pointer"
                    onClick={() => openLogDetail(log)}
                    data-testid={`audit-log-row-${log.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-wt-text-muted text-sm">
                        <Calendar className="h-4 w-4" />
                        {formatDate(log.created_at)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${config.bg}`}>
                          <Icon className={`h-4 w-4 ${config.color}`} />
                        </div>
                        <span className="text-wt-text-primary font-medium">
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-wt-gold font-mono">{log.po_number || '-'}</span>
                    </td>
                    <td className="p-4">
                      <p className="text-wt-text-primary text-sm max-w-xs truncate">
                        {log.details}
                      </p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-wt-text-muted text-sm">
                        <User className="h-4 w-4" />
                        {log.user_name || log.user_id || 'System'}
                      </div>
                    </td>
                    <td className="p-4">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-wt-text-muted hover:text-wt-gold"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {logs.length === 0 && (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
            <p className="text-wt-text-muted">No audit logs found</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="flex items-center justify-between p-4 border-t border-wt-border">
            <p className="text-sm text-wt-text-muted">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                className="border-wt-border text-wt-text-primary"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page * pagination.limit >= pagination.total}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                className="border-wt-border text-wt-text-primary"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <History className="h-5 w-5 text-wt-gold" />
              Audit Log Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-wt-subtle rounded-lg">
                  <p className="text-xs text-wt-text-muted">Action</p>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => {
                      const config = getActionConfig(selectedLog.action);
                      const Icon = config.icon;
                      return (
                        <>
                          <Icon className={`h-4 w-4 ${config.color}`} />
                          <span className="text-wt-text-primary font-medium">
                            {ACTION_LABELS[selectedLog.action] || selectedLog.action}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                <div className="p-3 bg-wt-subtle rounded-lg">
                  <p className="text-xs text-wt-text-muted">Timestamp</p>
                  <p className="text-wt-text-primary mt-1">{formatDate(selectedLog.created_at)}</p>
                </div>
              </div>
              
              {selectedLog.po_number && (
                <div className="p-3 bg-wt-subtle rounded-lg">
                  <p className="text-xs text-wt-text-muted">Purchase Order</p>
                  <p className="text-wt-gold font-mono mt-1">{selectedLog.po_number}</p>
                </div>
              )}
              
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-xs text-wt-text-muted">Details</p>
                <p className="text-wt-text-primary mt-1">{selectedLog.details}</p>
              </div>
              
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-xs text-wt-text-muted">Performed By</p>
                <div className="flex items-center gap-2 mt-1">
                  <User className="h-4 w-4 text-wt-text-muted" />
                  <span className="text-wt-text-primary">
                    {selectedLog.user_name || selectedLog.user_id || 'System'}
                  </span>
                </div>
              </div>
              
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-xs text-wt-text-muted">Event ID</p>
                <p className="text-wt-text-muted font-mono text-xs mt-1">{selectedLog.id}</p>
              </div>
              
              {selectedLog.po_id && (
                <Button
                  onClick={() => { setDetailDialogOpen(false); navigate(`/purchase-orders`); }}
                  className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Purchase Order
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>
        
        {/* Journey Logs Tab */}
        <TabsContent value="journeys" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <div className="flex gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                  <Input
                    placeholder="Search by phone number..."
                    value={journeyPhoneSearch}
                    onChange={(e) => setJourneyPhoneSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchJourneyByPhone()}
                    className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary w-full"
                  />
                </div>
                <Button onClick={searchJourneyByPhone} className="bg-wt-gold hover:bg-wt-gold-dark text-black">
                  <Search className="h-4 w-4 mr-1" />
                  Search
                </Button>
              </div>
              
              <Select value={journeyStatusFilter} onValueChange={setJourneyStatusFilter}>
                <SelectTrigger className="w-36 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="exited">Exited</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={journeyDaysFilter} onValueChange={setJourneyDaysFilter}>
                <SelectTrigger className="w-36 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Time Period" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="1">Last 24 Hours</SelectItem>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={loadJourneyLogs} 
              variant="outline" 
              className="border-wt-border text-wt-text-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Journey Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Send className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Total Executions</p>
                  <p className="text-2xl font-bold text-wt-text-primary">{journeyLogs.stats?.total_executions || journeyLogs.executions?.length || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Messages Sent</p>
                  <p className="text-2xl font-bold text-wt-text-primary">{journeyLogs.stats?.total_messages || journeyLogs.messages?.length || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Errors</p>
                  <p className="text-2xl font-bold text-wt-text-primary">{journeyLogs.stats?.total_errors || journeyLogs.errors?.length || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Failed</p>
                  <p className="text-2xl font-bold text-wt-text-primary">{journeyLogs.stats?.failed_executions || journeyLogs.executions?.filter(e => e.status === 'failed').length || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {journeyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Errors Section */}
              {journeyLogs.errors?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Errors Found ({journeyLogs.errors.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {journeyLogs.errors.map((error, idx) => (
                      <div key={idx} className="bg-wt-bg rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-start">
                          <span className="text-wt-text-primary font-medium">{error.journey_name || 'Unknown Journey'}</span>
                          <span className="text-wt-text-muted text-xs">{formatDate(error.timestamp)}</span>
                        </div>
                        <p className="text-red-400 mt-1">{error.error || 'Unknown error'}</p>
                        <p className="text-wt-text-muted text-xs mt-1">Node: {error.node_id} | Action: {error.action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Executions Table */}
              <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-wt-border">
                  <h3 className="text-wt-text-primary font-semibold">Journey Executions</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-wt-subtle">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Journey</th>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Phone</th>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Started</th>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Error</th>
                        <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-wt-border">
                      {journeyLogs.executions?.length > 0 ? (
                        journeyLogs.executions.map((execution) => (
                          <tr key={execution.id} className="hover:bg-wt-subtle/50">
                            <td className="px-4 py-3">
                              <span className="text-wt-text-primary font-medium">{execution.journey_name || 'Unknown'}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-wt-text-primary font-mono text-sm">
                                {execution.customer_phone || execution.entity_data?.phone || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={
                                execution.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                execution.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                execution.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-gray-500/20 text-gray-400'
                              }>
                                {execution.status}
                              </Badge>
                              {/* Show current node so an operator can tell at a glance
                                  whether the execution is stuck waiting on a delay
                                  vs blocked on a send — without opening the popup. */}
                              {execution.current_node_id && (
                                execution.status === 'in_progress'
                                || execution.status === 'waiting'
                              ) && (
                                <div className="text-xs text-wt-text-muted mt-1 font-mono">
                                  @{execution.current_node_id}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-wt-text-muted text-sm">
                              {formatDate(execution.entered_at || execution.started_at)}
                            </td>
                            <td className="px-4 py-3">
                              {(() => {
                                // Top-level error_message takes precedence; if empty, surface
                                // the last failed execution_log entry's error so operators can
                                // see "why this didn't send" without opening the detail popup.
                                const logs = execution.execution_log || [];
                                const lastFailed = [...logs].reverse().find(
                                  (l) => l && (l.status === 'failed' || l.error)
                                );
                                const errMsg = execution.error_message
                                  || (lastFailed && (lastFailed.error || lastFailed.details?.error))
                                  || null;
                                if (!errMsg) {
                                  return <span className="text-wt-text-muted">-</span>;
                                }
                                const text = String(errMsg);
                                return (
                                  <span
                                    className="text-red-400 text-sm truncate max-w-[240px] block"
                                    title={text}
                                  >
                                    {text.length > 60 ? `${text.substring(0, 60)}...` : text}
                                    {lastFailed?.node_id && (
                                      <span className="text-red-300/70 text-xs ml-1">
                                        @{lastFailed.node_id}
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-wt-text-muted hover:text-wt-text-primary"
                                onClick={() => {
                                  setSelectedExecution(execution);
                                  setExecutionDetailOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-wt-text-muted">
                            No journey executions found. Try searching by phone number or adjusting filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* WhatsApp Messages Table */}
              {journeyLogs.messages?.length > 0 && (
                <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-wt-border">
                    <h3 className="text-wt-text-primary font-semibold">WhatsApp Messages Sent via Journeys</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-wt-subtle">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Phone</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Template</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Journey</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Sent At</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Delivered</th>
                          <th className="px-4 py-3 text-left text-sm text-wt-text-muted font-medium">Read</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-wt-border">
                        {journeyLogs.messages.map((msg, idx) => (
                          <tr key={idx} className="hover:bg-wt-subtle/50">
                            <td className="px-4 py-3 font-mono text-sm text-wt-text-primary">{msg.phone}</td>
                            <td className="px-4 py-3 text-wt-text-primary">{msg.template_name}</td>
                            <td className="px-4 py-3 text-wt-text-muted">{msg.journey_name || '-'}</td>
                            <td className="px-4 py-3">
                              <Badge className={
                                msg.status === 'read' ? 'bg-green-500/20 text-green-400' :
                                msg.status === 'delivered' ? 'bg-blue-500/20 text-blue-400' :
                                msg.status === 'sent' ? 'bg-yellow-500/20 text-yellow-400' :
                                msg.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }>
                                {msg.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-wt-text-muted text-sm">{formatDate(msg.sent_at)}</td>
                            <td className="px-4 py-3 text-wt-text-muted text-sm">{msg.delivered_at ? formatDate(msg.delivered_at) : '-'}</td>
                            <td className="px-4 py-3 text-wt-text-muted text-sm">{msg.read_at ? formatDate(msg.read_at) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Execution Detail Dialog */}
          <Dialog open={executionDetailOpen} onOpenChange={setExecutionDetailOpen}>
            <DialogContent className="max-w-2xl bg-wt-paper border-wt-border text-wt-text-primary">
              <DialogHeader>
                <DialogTitle className="text-wt-text-primary">Execution Details</DialogTitle>
              </DialogHeader>
              {selectedExecution && (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-wt-text-muted text-sm">Journey</p>
                      <p className="text-wt-text-primary font-medium">{selectedExecution.journey_name || selectedExecution.journey_id}</p>
                    </div>
                    <div>
                      <p className="text-wt-text-muted text-sm">Status</p>
                      <Badge className={
                        selectedExecution.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        selectedExecution.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }>
                        {selectedExecution.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-wt-text-muted text-sm">Phone</p>
                      <p className="text-wt-text-primary font-mono">{selectedExecution.customer_phone || selectedExecution.entity_data?.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-wt-text-muted text-sm">Started At</p>
                      <p className="text-wt-text-primary">{formatDate(selectedExecution.entered_at || selectedExecution.started_at)}</p>
                    </div>
                  </div>

                  {/* "Why didn't this send?" diagnostics panel —
                      auto-fetched when popup opens. Surfaces
                      template approval status, 24-h window, cooldown,
                      BSP/Meta errors, related WhatsApp messages. */}
                  <div className="bg-wt-subtle/40 border border-wt-border rounded-lg p-3 space-y-3" data-testid="diagnostics-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-wt-text-primary font-semibold text-sm">Diagnostics — why this did/didn't send</p>
                      {diagnosticsLoading && <span className="text-wt-text-muted text-xs">Loading…</span>}
                    </div>

                    {!diagnosticsLoading && diagnostics?.reasons?.length > 0 && (
                      <ul className="space-y-1.5">
                        {diagnostics.reasons.map((r, idx) => (
                          <li key={idx} className={`text-sm ${
                            r.severity === 'error' ? 'text-red-300' :
                            r.severity === 'warn' ? 'text-yellow-300' :
                            r.severity === 'ok' ? 'text-green-300' :
                            'text-wt-text-primary'
                          }`}>
                            <span className="font-medium">{r.label}</span>
                            {r.hint && <span className="block text-xs text-wt-text-muted mt-0.5">{r.hint}</span>}
                          </li>
                        ))}
                      </ul>
                    )}

                    {!diagnosticsLoading && diagnostics?.template_statuses?.length > 0 && (
                      <div>
                        <p className="text-wt-text-muted text-xs mb-1">Templates referenced</p>
                        <div className="flex flex-wrap gap-2">
                          {diagnostics.template_statuses.map((t, idx) => {
                            const s = (t.status || 'unknown').toUpperCase();
                            const isOk = s === 'APPROVED' || s === 'ACTIVE';
                            return (
                              <span key={idx} className={`text-xs px-2 py-0.5 rounded font-mono ${
                                isOk ? 'bg-green-500/15 text-green-300' :
                                s === 'NOT_FOUND' ? 'bg-red-500/15 text-red-300' :
                                'bg-yellow-500/15 text-yellow-300'
                              }`}>
                                {t.name} · {s}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {!diagnosticsLoading && diagnostics?.related_messages?.length > 0 && (
                      <div>
                        <p className="text-wt-text-muted text-xs mb-1">Related WhatsApp messages</p>
                        <div className="space-y-1">
                          {diagnostics.related_messages.slice(0, 5).map((m, idx) => (
                            <div key={idx} className="text-xs bg-wt-paper rounded p-2 flex items-center justify-between">
                              <span className="font-mono text-wt-text-primary">{m.template_name || '-'}</span>
                              <span className="text-wt-text-muted">{m.node_id || '-'}</span>
                              <span className={`px-1.5 py-0.5 rounded ${
                                m.status === 'read' ? 'bg-green-500/15 text-green-300' :
                                m.status === 'delivered' ? 'bg-blue-500/15 text-blue-300' :
                                m.status === 'sent' ? 'bg-yellow-500/15 text-yellow-300' :
                                m.status === 'failed' ? 'bg-red-500/15 text-red-300' :
                                'bg-gray-500/15 text-gray-300'
                              }`}>{m.status || '?'}</span>
                              <span className="text-wt-text-muted">{m.sent_at ? formatDate(m.sent_at) : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {diagnostics?.cooldown_block && (
                      <p className="text-yellow-300 text-xs">
                        Cooldown active — last execution at {formatDate(diagnostics.cooldown_block.blocked_by_entered_at)} ({diagnostics.cooldown_block.cooldown_hours}h window)
                      </p>
                    )}
                  </div>

                  {selectedExecution.error_message && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <p className="text-red-400 font-medium mb-1">Error Message</p>
                      <p className="text-red-300 text-sm">{selectedExecution.error_message}</p>
                    </div>
                  )}
                  
                  {selectedExecution.execution_log?.length > 0 && (
                    <div>
                      <p className="text-wt-text-muted text-sm mb-2">Execution Log</p>
                      <div className="space-y-2">
                        {selectedExecution.execution_log.map((log, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${log.status === 'failed' ? 'bg-red-500/10' : 'bg-wt-subtle'}`}>
                            <div className="flex justify-between items-start">
                              <span className="font-medium text-wt-text-primary">{log.action}</span>
                              <Badge className={log.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                                {log.status}
                              </Badge>
                            </div>
                            <p className="text-wt-text-muted text-xs mt-1">Node: {log.node_id}</p>
                            <p className="text-wt-text-muted text-xs">{formatDate(log.timestamp)}</p>
                            {log.error && <p className="text-red-400 text-sm mt-1">{log.error}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* Webhook Logs Tab */}
        <TabsContent value="webhooks" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <Select value={webhookSourceFilter} onValueChange={setWebhookSourceFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="mamopay">MamoPay</SelectItem>
                  <SelectItem value="shopify">Shopify</SelectItem>
                  <SelectItem value="zoho">Zoho</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={webhookDaysFilter} onValueChange={setWebhookDaysFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Time Period" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="1">Last 24 Hours</SelectItem>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={loadWebhookLogs} 
              variant="outline" 
              className="border-wt-border text-wt-text-primary"
              disabled={webhookLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${webhookLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* Source Stats */}
          {Object.keys(webhookStats).length > 0 && (
            <div className="flex gap-4 flex-wrap">
              {Object.entries(webhookStats).map(([source, count]) => {
                const config = getSourceConfig(source);
                const Icon = config.icon;
                return (
                  <div key={source} className={`px-3 py-2 rounded-lg ${config.bg} flex items-center gap-2`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}: {count}</span>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Webhook Logs Table */}
          <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
            {webhookLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
              </div>
            ) : webhookLogs.length === 0 ? (
              <div className="text-center py-12">
                <Webhook className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-30" />
                <p className="text-wt-text-muted">No webhook logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-wt-subtle">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-wt-text-primary">Source</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-wt-text-primary">Received At</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-wt-text-primary">Event/Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-wt-text-primary">Details</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-wt-text-primary">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-wt-border">
                    {webhookLogs.map((log) => {
                      const config = getSourceConfig(log.source);
                      const Icon = config.icon;
                      const payload = log.payload || {};
                      // Webhook log rows come from TWO collections: generic
                      // `webhook_logs` (where event/details live in `payload`)
                      // and `shopify_webhooks` (where they live as top-level
                      // fields like `topic`, `status`, `error`, `shop_domain`).
                      // Pick the right field per source so neither shows N/A.
                      const eventLabel =
                        log.event_type ||
                        log.topic ||
                        payload.event ||
                        payload.type ||
                        payload.event_type ||
                        'unknown';
                      const statusValue = (log.status || payload.status || '').toLowerCase();
                      const detailText =
                        log.error ||
                        payload.link_id ||
                        payload.order_id ||
                        payload.external_id ||
                        log.shop_domain ||
                        log.webhook_id ||
                        '—';
                      const statusBadgeClass =
                        statusValue === 'failed' || log.error
                          ? 'bg-red-500/20 text-red-300 border-red-500/40'
                          : statusValue === 'success' || log.processed
                            ? 'bg-green-500/20 text-green-300 border-green-500/40'
                            : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40';
                      
                      return (
                        <tr key={log.id} className="hover:bg-wt-subtle/50" data-testid={`webhook-log-row-${log.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded ${config.bg}`}>
                                <Icon className={`h-4 w-4 ${config.color}`} />
                              </div>
                              <span className="text-sm font-medium text-wt-text-primary">{config.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-wt-text-secondary">{formatDate(log.received_at)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-wt-subtle text-wt-text-primary border-wt-border w-fit font-mono text-xs">
                                {eventLabel}
                              </Badge>
                              <Badge className={`${statusBadgeClass} w-fit text-[10px]`}>
                                {statusValue || (log.processed ? 'success' : log.error ? 'failed' : 'pending')}
                              </Badge>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm truncate max-w-xs block ${log.error ? 'text-red-300' : 'text-wt-text-muted'}`}>
                              {detailText}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedWebhookLog(log); setWebhookDetailOpen(true); }}
                              className="text-wt-gold hover:text-wt-gold-hover"
                              data-testid={`webhook-log-detail-${log.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Webhook Detail Dialog */}
          <Dialog open={webhookDetailOpen} onOpenChange={setWebhookDetailOpen}>
            <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-wt-text-primary flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-wt-gold" />
                  Webhook Payload
                </DialogTitle>
              </DialogHeader>
              
              {selectedWebhookLog && (
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-wt-subtle rounded-lg">
                      <p className="text-xs text-wt-text-muted">Source</p>
                      <p className="text-wt-text-primary mt-1 font-medium">{selectedWebhookLog.source}</p>
                    </div>
                    <div className="p-3 bg-wt-subtle rounded-lg">
                      <p className="text-xs text-wt-text-muted">Received At</p>
                      <p className="text-wt-text-primary mt-1">{formatDate(selectedWebhookLog.received_at)}</p>
                    </div>
                    {(selectedWebhookLog.event_type || selectedWebhookLog.topic) && (
                      <div className="p-3 bg-wt-subtle rounded-lg">
                        <p className="text-xs text-wt-text-muted">Event / Topic</p>
                        <p className="text-wt-text-primary mt-1 font-mono text-sm">
                          {selectedWebhookLog.event_type || selectedWebhookLog.topic}
                        </p>
                      </div>
                    )}
                    {selectedWebhookLog.shop_domain && (
                      <div className="p-3 bg-wt-subtle rounded-lg">
                        <p className="text-xs text-wt-text-muted">Shop Domain</p>
                        <p className="text-wt-text-primary mt-1 text-sm">{selectedWebhookLog.shop_domain}</p>
                      </div>
                    )}
                    {selectedWebhookLog.webhook_id && (
                      <div className="p-3 bg-wt-subtle rounded-lg">
                        <p className="text-xs text-wt-text-muted">Webhook ID</p>
                        <p className="text-wt-text-primary mt-1 text-xs font-mono break-all">{selectedWebhookLog.webhook_id}</p>
                      </div>
                    )}
                    {('hmac_present' in selectedWebhookLog) && (
                      <div className="p-3 bg-wt-subtle rounded-lg">
                        <p className="text-xs text-wt-text-muted">HMAC Header</p>
                        <p className={`mt-1 text-sm ${selectedWebhookLog.hmac_present ? 'text-green-300' : 'text-yellow-300'}`}>
                          {selectedWebhookLog.hmac_present ? 'present' : 'missing'}
                        </p>
                      </div>
                    )}
                  </div>

                  {selectedWebhookLog.error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg">
                      <p className="text-xs text-red-300 font-semibold mb-1">Error</p>
                      <p className="text-red-200 text-sm">{selectedWebhookLog.error}</p>
                      {String(selectedWebhookLog.error).toLowerCase().includes('signature') && (
                        <p className="text-xs text-red-300/80 mt-2 leading-relaxed">
                          HMAC signature verification failed. Most common cause: the Shopify
                          app credentials (Client Secret) used to register this webhook differ
                          from the ones currently saved on this store. Disconnect &amp; reconnect
                          the Shopify store from the integration page to issue a fresh token
                          tied to the current credentials.
                        </p>
                      )}
                    </div>
                  )}

                  {selectedWebhookLog.payload ? (
                    <div className="p-3 bg-wt-subtle rounded-lg">
                      <p className="text-xs text-wt-text-muted mb-2">Full Payload (JSON)</p>
                      <pre className="text-xs text-wt-text-secondary bg-wt-bg p-3 rounded overflow-x-auto max-h-96">
                        {JSON.stringify(selectedWebhookLog.payload, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="p-3 bg-wt-subtle/40 rounded-lg text-xs text-wt-text-muted">
                      Payload was not stored for this webhook (typically because it failed
                      verification before parsing). Fix the underlying error above and the
                      next webhook will be persisted with its full body.
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* Shopify Sync Logs Tab — answers "why isn't this order
            fulfilled on Shopify?" pulled from the shared
            `order_audit_logs` collection via
            `/api/admin/shopify-sync-logs`. Includes the silent-skip
            paths (no open fulfillment order, missing shopify_order_id)
            that previously gave no user-visible feedback. */}
        <TabsContent value="shopify_sync" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <Select value={shopifyStatusFilter} onValueChange={setShopifyStatusFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="shopify-sync-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-subtle border-wt-border">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed / Skipped</SelectItem>
                </SelectContent>
              </Select>
              <Select value={shopifyDaysFilter} onValueChange={setShopifyDaysFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-subtle border-wt-border">
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                <Input
                  placeholder="Order #, AWB, or error text…"
                  value={shopifySearch}
                  onChange={(e) => setShopifySearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') loadShopifyLogs(); }}
                  className="pl-9 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="shopify-sync-search-input"
                />
              </div>
            </div>
            <Button
              onClick={loadShopifyLogs}
              variant="outline"
              className="border-wt-border text-wt-text-primary"
              disabled={shopifyLoading}
              data-testid="shopify-sync-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${shopifyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Summary badges */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-green-500/10 border border-green-500/30">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span className="text-sm text-wt-text-primary">
                Success: <span className="font-semibold">{shopifyStatusCounts.success || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <span className="text-sm text-wt-text-primary">
                Failed/Skipped: <span className="font-semibold">{shopifyStatusCounts.failed || 0}</span>
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-wt-border overflow-hidden">
            <table className="w-full text-sm" data-testid="shopify-sync-table">
              <thead className="bg-wt-subtle">
                <tr className="text-left text-wt-text-muted">
                  <th className="p-3 font-medium">Time</th>
                  <th className="p-3 font-medium">Order</th>
                  <th className="p-3 font-medium">Payment</th>
                  <th className="p-3 font-medium">Courier</th>
                  <th className="p-3 font-medium">AWB</th>
                  <th className="p-3 font-medium">Result</th>
                  <th className="p-3 font-medium">Reason</th>
                  <th className="p-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {shopifyLogs.length === 0 && !shopifyLoading && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-wt-text-muted">
                      No Shopify sync attempts in this window.
                    </td>
                  </tr>
                )}
                {shopifyLogs.map((log) => {
                  const failed = log.status === 'failed';
                  const skipped = log.action_type === 'shopify_fulfillment_skipped';
                  return (
                    <tr
                      key={log.id}
                      className="border-t border-wt-border hover:bg-wt-subtle/50 transition-colors"
                      data-testid={`shopify-sync-row-${log.id}`}
                    >
                      <td className="p-3 text-wt-text-muted whitespace-nowrap">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                      </td>
                      <td className="p-3 text-wt-text-primary font-mono">
                        {log.order_number || '-'}
                      </td>
                      <td className="p-3">
                        {log.payment_bucket === 'paid' && (
                          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Paid</Badge>
                        )}
                        {log.payment_bucket === 'cod_or_pending' && (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">COD / Pending</Badge>
                        )}
                        {log.payment_bucket && log.payment_bucket !== 'paid' && log.payment_bucket !== 'cod_or_pending' && (
                          <Badge variant="outline" className="border-wt-border">{log.payment_bucket}</Badge>
                        )}
                      </td>
                      <td className="p-3 text-wt-text-primary capitalize">
                        {log.courier_type || '-'}
                      </td>
                      <td className="p-3 text-wt-text-muted font-mono text-xs">
                        {log.awb_number || '-'}
                      </td>
                      <td className="p-3">
                        {failed ? (
                          skipped ? (
                            <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/30">
                              Skipped
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/15 text-red-300 border-red-500/30">
                              Failed
                            </Badge>
                          )
                        ) : (
                          <Badge className="bg-green-500/15 text-green-300 border-green-500/30">
                            Success
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-wt-text-muted text-xs max-w-md truncate">
                        {log.error || log.notes || '-'}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-wt-gold hover:bg-wt-gold/10"
                          onClick={() => { setSelectedShopifyLog(log); setShopifyDetailOpen(true); }}
                          data-testid={`shopify-sync-view-${log.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail dialog */}
          <Dialog open={shopifyDetailOpen} onOpenChange={setShopifyDetailOpen}>
            <DialogContent className="bg-wt-bg border-wt-border text-wt-text-primary max-w-3xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-wt-gold" />
                  Shopify Sync — {selectedShopifyLog?.order_number || '—'}
                </DialogTitle>
              </DialogHeader>
              {selectedShopifyLog && (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-wt-text-muted">Action:</span> <code>{selectedShopifyLog.action_type}</code></div>
                    <div><span className="text-wt-text-muted">Courier:</span> {selectedShopifyLog.courier_type || '-'}</div>
                    <div><span className="text-wt-text-muted">AWB:</span> <code>{selectedShopifyLog.awb_number || '-'}</code></div>
                    <div><span className="text-wt-text-muted">Payment:</span> {selectedShopifyLog.payment_bucket || '-'}</div>
                    <div><span className="text-wt-text-muted">Shopify Order:</span> <code>{selectedShopifyLog.shopify_order_id || '-'}</code></div>
                    <div><span className="text-wt-text-muted">Fulfillment ID:</span> <code>{selectedShopifyLog.fulfillment_id || '-'}</code></div>
                  </div>
                  {selectedShopifyLog.error && (
                    <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30">
                      <div className="text-xs text-red-400 font-semibold mb-1">Error</div>
                      <div className="text-red-200">{selectedShopifyLog.error}</div>
                    </div>
                  )}
                  {selectedShopifyLog.notes && !selectedShopifyLog.error && (
                    <div className="p-3 rounded-md bg-wt-subtle border border-wt-border">
                      <div className="text-xs text-wt-text-muted font-semibold mb-1">Notes</div>
                      <div>{selectedShopifyLog.notes}</div>
                    </div>
                  )}
                  <details>
                    <summary className="cursor-pointer text-wt-text-muted text-xs">Raw payload</summary>
                    <pre className="mt-2 p-3 rounded-md bg-wt-subtle text-xs overflow-auto max-h-80">
                      {JSON.stringify(selectedShopifyLog.raw, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Channel Sync Tab (Danube / Noon) */}
        <TabsContent value="channel_sync" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="channel-sync-channel-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-subtle border-wt-border">
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="danube">Danube</SelectItem>
                  <SelectItem value="noon">Noon</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelStatusFilter} onValueChange={setChannelStatusFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="channel-sync-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-subtle border-wt-border">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelDaysFilter} onValueChange={setChannelDaysFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-subtle border-wt-border">
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={loadChannelLogs}
              variant="outline"
              className="border-wt-border text-wt-text-primary"
              disabled={channelLoading}
              data-testid="channel-sync-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${channelLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-green-500/10 border border-green-500/30">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span className="text-sm text-wt-text-primary">
                Success: <span className="font-semibold">{channelStatusCounts.success || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <span className="text-sm text-wt-text-primary">
                Failed: <span className="font-semibold">{channelStatusCounts.failed || 0}</span>
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-wt-border overflow-hidden">
            <table className="w-full text-sm" data-testid="channel-sync-table">
              <thead className="bg-wt-subtle">
                <tr className="text-left text-wt-text-muted">
                  <th className="p-3 font-medium">Time</th>
                  <th className="p-3 font-medium">Channel</th>
                  <th className="p-3 font-medium">Type</th>
                  <th className="p-3 font-medium">Trigger</th>
                  <th className="p-3 font-medium">Warehouse / Order</th>
                  <th className="p-3 font-medium">Pushed</th>
                  <th className="p-3 font-medium">Result</th>
                  <th className="p-3 font-medium">Errors</th>
                </tr>
              </thead>
              <tbody>
                {channelLogs.length === 0 && !channelLoading && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-wt-text-muted">
                      No channel sync activity in this window.
                    </td>
                  </tr>
                )}
                {channelLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-t border-wt-border hover:bg-wt-subtle/50 transition-colors"
                    data-testid={`channel-sync-row-${log.id}`}
                  >
                    <td className="p-3 text-wt-text-muted whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="border-wt-border capitalize">{log.channel}</Badge>
                    </td>
                    <td className="p-3 text-wt-text-primary">
                      {log.type === 'order_push' ? 'Order → Shopify' : 'Stock Sync'}
                    </td>
                    <td className="p-3 text-wt-text-muted capitalize">
                      {(log.trigger || '-').replace(/_/g, ' ')}
                    </td>
                    <td className="p-3 text-wt-text-primary font-mono text-xs">
                      {log.type === 'order_push' ? (log.order_number || '-') : ((log.locations || []).join(', ') || '-')}
                    </td>
                    <td className="p-3 text-wt-text-primary">
                      {log.type === 'order_push' ? '-' : `${log.pushed || 0} / ${log.total || 0}`}
                    </td>
                    <td className="p-3">
                      {log.status === 'failed' ? (
                        <Badge className="bg-red-500/15 text-red-300 border-red-500/30">Failed</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Success</Badge>
                      )}
                    </td>
                    <td className="p-3 text-red-300 text-xs max-w-xs">
                      {(log.errors || []).filter(Boolean).slice(0, 2).join('; ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        
        {/* System Logs Tab */}
        <TabsContent value="system" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <Select value={systemCategoryFilter} onValueChange={setSystemCategoryFilter}>
                <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="mamopay">MamoPay</SelectItem>
                  <SelectItem value="shopify">Shopify</SelectItem>
                  <SelectItem value="zoho">Zoho</SelectItem>
                  <SelectItem value="webhook">Webhooks</SelectItem>
                  <SelectItem value="order">Orders</SelectItem>
                  <SelectItem value="inventory">Inventory</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={systemLevelFilter} onValueChange={setSystemLevelFilter}>
                <SelectTrigger className="w-36 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="ERROR">Errors</SelectItem>
                  <SelectItem value="WARNING">Warnings</SelectItem>
                  <SelectItem value="INFO">Info</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                <Input
                  placeholder="Search logs..."
                  value={systemSearchTerm}
                  onChange={(e) => setSystemSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadSystemLogs()}
                  className="pl-10 w-48 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <Button 
              onClick={loadSystemLogs} 
              variant="outline" 
              className="border-wt-border text-wt-text-primary"
              disabled={systemLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${systemLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* System Logs List */}
          <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
            {systemLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
              </div>
            ) : systemLogs.length === 0 ? (
              <div className="text-center py-12">
                <Terminal className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-30" />
                <p className="text-wt-text-muted">No system logs found</p>
              </div>
            ) : (
              <div className="divide-y divide-wt-border max-h-[600px] overflow-y-auto">
                {systemLogs.map((log, idx) => (
                  <div key={idx} className="px-4 py-2 hover:bg-wt-subtle/50 font-mono text-xs">
                    <div className="flex items-start gap-3">
                      <Badge className={`shrink-0 ${LEVEL_COLORS[log.level] || LEVEL_COLORS.INFO}`}>
                        {log.level}
                      </Badge>
                      <span className="text-wt-text-muted shrink-0">{log.timestamp || ''}</span>
                      <span className="text-wt-text-secondary break-all">{log.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Integration Status Tab */}
        <TabsContent value="status" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={loadIntegrationStatus} 
              variant="outline" 
              className="border-wt-border text-wt-text-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Status
            </Button>
          </div>
          
          {integrationStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Shopify Status */}
              <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${integrationStatus.shopify?.connected ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <ShoppingBag className={`h-6 w-6 ${integrationStatus.shopify?.connected ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-wt-text-primary">Shopify</h3>
                    <Badge className={integrationStatus.shopify?.connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {integrationStatus.shopify?.connected ? 'Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                </div>
                {integrationStatus.shopify?.connected && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Store:</span>
                      <span className="text-wt-text-primary">{integrationStatus.shopify.shop_domain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Locations Cached:</span>
                      <span className="text-wt-text-primary">{integrationStatus.shopify.locations_cached}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Last Sync:</span>
                      <span className="text-wt-text-primary">{integrationStatus.shopify.last_sync ? formatDate(integrationStatus.shopify.last_sync) : 'Never'}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* MamoPay Status */}
              <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${integrationStatus.mamopay?.connected ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <CreditCard className={`h-6 w-6 ${integrationStatus.mamopay?.connected ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-wt-text-primary">MamoPay</h3>
                    <Badge className={integrationStatus.mamopay?.connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {integrationStatus.mamopay?.connected ? 'Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                </div>
                {integrationStatus.mamopay?.connected && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Webhook URL:</span>
                      <span className="text-wt-text-primary text-xs truncate max-w-32" title={integrationStatus.mamopay.webhook_url}>
                        {integrationStatus.mamopay.webhook_url || 'Not configured'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Last Webhook:</span>
                      <span className="text-wt-text-primary">{integrationStatus.mamopay.last_webhook ? formatDate(integrationStatus.mamopay.last_webhook) : 'Never'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Errors (24h):</span>
                      <span className={integrationStatus.mamopay.errors_24h > 0 ? 'text-red-400' : 'text-green-400'}>
                        {integrationStatus.mamopay.errors_24h || 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Zoho Status */}
              <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${integrationStatus.zoho?.connected ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <CloudUpload className={`h-6 w-6 ${integrationStatus.zoho?.connected ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-wt-text-primary">Zoho Books</h3>
                    <Badge className={integrationStatus.zoho?.connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {integrationStatus.zoho?.connected ? 'Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                </div>
                {integrationStatus.zoho?.connected && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-wt-text-muted">Organization ID:</span>
                      <span className="text-wt-text-primary">{integrationStatus.zoho.organization_id}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditLogPage;
